import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthStore } from '../../stores/authStore';
import { COLLECTIONS } from '../../lib/collections';
import { moderateContent } from '../../lib/functions';

type ContentType = 'business' | 'pitch' | 'story' | 'artwork';

interface PublishModalProps {
  visible: boolean;
  onClose: () => void;
  defaultCaption?: string;
  defaultContentType?: ContentType;
  planId?: string;
  businessName?: string;
  planText?: string;
  planAnswers?: Record<string, Record<string, string>>;
  explorerName?: string;
}

const CONTENT_TYPES: { key: ContentType; label: string; emoji: string }[] = [
  { key: 'business', label: 'Business', emoji: '🏢' },
  { key: 'pitch', label: 'Pitch', emoji: '🎤' },
  { key: 'story', label: 'Story', emoji: '📖' },
  { key: 'artwork', label: 'Artwork', emoji: '🎨' },
];

export default function PublishModal({
  visible,
  onClose,
  defaultCaption = '',
  defaultContentType = 'business',
  planId,
  businessName,
  planText,
  planAnswers,
  explorerName,
}: PublishModalProps) {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);

  const [contentType, setContentType] = useState<ContentType>(defaultContentType);
  const [caption, setCaption] = useState(defaultCaption);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handlePublish = async () => {
    if (!user || !profile || !caption.trim()) return;
    setPublishing(true);
    setError(null);

    try {
      // Moderation check
      try {
        const modResult = await moderateContent({ text: caption, userId: user.uid });
        if (!modResult.data.safe) {
          setError('Your content didn\'t pass our safety check. Please revise and try again.');
          setPublishing(false);
          return;
        }
      } catch {
        // If moderation service unavailable in dev, allow through
      }

      // Write to barkBoard (Spotlight)
      const postId = `${user.uid}_${Date.now()}`;
      const ref = doc(db, COLLECTIONS.SPOTLIGHT, postId);
      await setDoc(ref, {
        contentType,
        caption: caption.trim(),
        creatorId: user.uid,
        creatorName: profile.explorerName || explorerName || 'Anonymous',
        businessName: businessName || '',
        planId: planId || '',
        planText: planText || '',
        planAnswers: planAnswers || {},
        complimentCount: 0,
        createdAt: serverTimestamp(),
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setCaption('');
        onClose();
      }, 1500);
    } catch (e) {
      console.error(e);
      setError('Failed to publish. Please try again.');
    }
    setPublishing(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.section
            role="region"
            aria-label="Share to Spotlight"
            initial={{ scale: 0.95, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className="w-full max-w-md rounded-lg border border-zinc-700/60 bg-zinc-900 p-6 shadow-xl flex flex-col gap-5 text-zinc-100"
          >
            {success ? (
              <div className="py-8 text-center">
                <span className="text-5xl">🎉</span>
                <h3 className="mt-3 text-xl font-semibold text-zinc-100">Published!</h3>
                <p className="mt-1 text-sm text-zinc-400">Your post is live on Spotlight</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <header className="flex items-start justify-between">
                  <div className="space-y-0.5">
                    <h2 className="text-lg font-semibold">Share to Spotlight</h2>
                    <p className="text-sm text-zinc-400">What are you sharing?</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-md border border-zinc-700 bg-zinc-800 px-2.5 py-1.5 text-sm text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </header>

                {/* Content type selector */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    {CONTENT_TYPES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setContentType(t.key)}
                        className={`min-h-[56px] flex-1 rounded-md border py-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 ${
                          contentType === t.key
                            ? 'border-brand-500 bg-brand-500 text-white'
                            : 'border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                        }`}
                      >
                        <span className="block text-base">{t.emoji}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Caption */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-zinc-300">Caption</label>
                  <div className="rounded-md border border-zinc-700 bg-zinc-800 focus-within:ring-2 focus-within:ring-zinc-500 focus-within:ring-offset-2 focus-within:ring-offset-zinc-900">
                    <textarea
                      rows={3}
                      maxLength={280}
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder={`I just finished my business plan for ${businessName || 'my business'}! 🚀`}
                      className="w-full rounded-md bg-transparent px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none resize-none"
                    />
                  </div>
                  <p className="text-right text-xs text-zinc-500">{caption.length}/280</p>
                </div>

                {error && (
                  <div
                    role="alert"
                    className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400"
                  >
                    {error}
                  </div>
                )}

                {/* Actions */}
                <motion.button
                  onClick={handlePublish}
                  disabled={publishing || !caption.trim()}
                  whileTap={{ scale: 0.98 }}
                  className="w-full rounded-md bg-brand-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
                >
                  {publishing ? 'Publishing…' : 'Publish'}
                </motion.button>
                <button
                  onClick={onClose}
                  className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500"
                >
                  Cancel
                </button>
              </>
            )}
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
